import React, { FunctionComponent } from "react";
import { useIntl } from "react-intl";
import useCustomizationContext from "../../../../hooks/useCustomizationContext";
import { ReactComponent as IconPen } from "../../../../icons/edit-2.svg";
import { ReactComponent as IconImage } from "../../../../icons/image.svg";
import { ReactComponent as IconTrash } from "../../../../icons/trash.svg";
import { ReactComponent as IconRotate } from "../../../../icons/rotate-cw.svg";
import { Panel } from "../../../../states/customization";
import { Content } from "../../../../types/customization/content";
import {
  getCoverFilteredPages,
  getNoCoverDefinitions,
} from "../../../../utils/customization/cover";
import { findPageAndDefinition } from "../../../../utils/customization/finders";
import {
  buildRotationPagesMapping,
  hasEmptyEditablePictures,
  isEmptyPage,
  RotationPagesMapping,
} from "../../../../utils/customization/page";
import { hasLayoutWithMorePictures } from "../../../../utils/customization/template";
import "./style.scss";

interface Props {
  content: Content;
  displayPanelCondition: boolean;
  isRotationPair: false | RotationPagesMapping;
  pageKey: string;
}

const PageBar: FunctionComponent<Props> = ({
  content,
  displayPanelCondition,
  isRotationPair,
  pageKey,
}) => {
  const intl = useIntl();
  const {
    dispatch,
    template,
    configuration,
    ui: { panel },
  } = useCustomizationContext();
  const { page, pageDefinition } = findPageAndDefinition(pageKey, template, content);

  const noCoverPageDefinitions = getNoCoverDefinitions(template.pageDefinitions);
  const noCoverContentPages = getCoverFilteredPages(content.pages, noCoverPageDefinitions);
  const minPages = (configuration && configuration.pages && configuration.pages.min) || 1;
  const packIncrement = configuration && configuration.pages && configuration.pages.increment;
  const hasPack = (packIncrement && packIncrement > 1) || false;
  const hasAdditionnalPages =
    packIncrement && noCoverContentPages.length >= minPages + packIncrement;
  const shouldRemoveMultiple =
    template.templateType === "book" || (template.templateType === "mural" && hasPack);

  let removeItem: JSX.Element | null = null;
  if (content.templateType === "print" || (isEmptyPage(page) && content.pages.length === 1)) {
    // no removeItem
  } else if (template.templateType !== "book" || hasAdditionnalPages) {
    const onRemoveClick = () => {
      if (
        panel.key &&
        panel.key === pageKey &&
        (panel.current === Panel.EDITPAGE || panel.current === Panel.PAGEOPTIONS)
      ) {
        dispatch({ type: "setPanel", panel: Panel.CLOSED });
      }
      if (shouldRemoveMultiple) {
        dispatch({
          mode: "deletion",
          opened: true,
          type: "setOrganizePageList",
        });
      } else {
        dispatch({ type: "removePage", pageKey });
      }
    };

    removeItem = (
      <div className="PageBar-item" onClick={onRemoveClick}>
        <IconTrash />
      </div>
    );
  }

  const handleOnClickOptions = () => {
    dispatch({
      key: pageKey,
      panel: Panel.EDITPAGE,
      type: "setPanel",
    });
  };

  const handleOnClickAddPictures = () => {
    dispatch({
      open: true,
      pageKey,
      type: "setGallery",
    });
  };

  const hasEmptyPictures = hasEmptyEditablePictures(page, template);
  const layoutWithMorePictures = hasLayoutWithMorePictures(
    page.pageDefinitionKey,
    template.pageDefinitions
  );
  const hasMorePicturesButton = hasEmptyPictures || layoutWithMorePictures;
  const rotationPairsMapping = buildRotationPagesMapping(template.pageDefinitions);

  const handleOnClickRotate = () => {
    dispatch({
      pageDefinitionKey: rotationPairsMapping[pageDefinition.key].counterpartKey,
      pageKey,
      type: "changePagePageDefinition",
    });
  };

  return (
    <div className="PageBar">
      <div className="PageBar-items">
        {hasMorePicturesButton && (
          <div className="PageBar-item" onClick={handleOnClickAddPictures}>
            <IconImage />
          </div>
        )}

        {displayPanelCondition && (
          <div
            className="PageBar-item"
            onClick={handleOnClickOptions}
            title={intl.formatMessage({ id: "kustom_FloatingMenu_layout" })}
          >
            <IconPen />
          </div>
        )}

        {isRotationPair && (
          <div
            className="PageBar-item"
            onClick={handleOnClickRotate}
            title={intl.formatMessage({ id: "kustom_FloatingMenu_layout" })}
          >
            <IconRotate />
          </div>
        )}

        {!hasMorePicturesButton && removeItem}
      </div>
    </div>
  );
};

export default PageBar;
